import { useEffect, useState } from "react"
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react"
import { cn } from "@/lib/utils"
import * as outreachApi from "../api/outreach.api"
import { ChannelPauseControl } from "./ChannelPauseControl"
import { formatInTz } from "../lib/time"
import type { PauseState } from "../types/outreach.types"

interface Props {
  orgId: string
  orgName: string
}

/** Clinic-wide outreach switch: GET /outreach/pause?scope=org + the pause toggles. */
export function OrgPauseStrip({ orgId, orgName }: Props) {
  const [state, setState] = useState<PauseState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    outreachApi
      .getPause("org", orgId)
      .then((s) => !cancelled && setState(s))
      .catch((err) => !cancelled && setError(outreachApi.errorMessage(err)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [orgId])

  const paused = !!state?.paused_channels

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border px-4 py-3 md:flex-row md:items-center md:justify-between",
        paused ? "border-warning/40 bg-warning/10" : "bg-card",
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {paused ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        ) : (
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 text-sm">
          <p className="font-medium">
            {orgName} outreach{" "}
            {loading
              ? ""
              : paused
                ? state?.paused_channels === "ALL"
                  ? "is paused: no texts or calls"
                  : state?.paused_channels === "SMS"
                    ? "is paused for texts"
                    : "is paused for calls"
                : "is on"}
          </p>
          {error ? (
            <p className="text-xs text-destructive">Could not load pause state: {error}</p>
          ) : paused ? (
            <p className="text-xs text-muted-foreground break-words">
              {state?.reason ? `"${state.reason}"` : "No reason given"}
              {state?.paused_by ? ` · by ${state.paused_by}` : ""}
              {state?.paused_at ? ` · since ${formatInTz(state.paused_at)}` : ""}
              {state?.pause_end ? ` · until ${formatInTz(state.pause_end)}` : ""}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Clinic-wide switch. It covers every campaign and every patient at this clinic.
            </p>
          )}
        </div>
      </div>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <ChannelPauseControl
          scope="org"
          targetId={orgId}
          subject={`every patient at ${orgName}`}
          pausedChannels={state?.paused_channels}
          currentReason={state?.reason}
          onChange={setState}
          disabled={!!error}
        />
      )}
    </div>
  )
}
